"use client";

import type { ReactElement, ReactNode } from "react";
import { useMemo } from "react";
import { useTweaks } from "@/components/dev-tools/tweaks2";
import { resolveTweakedVariant } from "@/components/dev-tools/tweaksVariantResolution";
import {
  DEFAULT_RAIL_EDIT_CONTEXT_VARIANT,
  DEFAULT_RAIL_EXPANSION_VARIANT,
  DEFAULT_RAIL_FOLD_SURFACE_VARIANT,
  DEFAULT_RAIL_PULSE_VARIANT,
  RailEditContextVariant,
  RailExpansionVariant,
  RailFoldSurfaceVariant,
  RailPulseVariant,
} from "@/components/ui/Cteamsgallery/gallery.constants";
import {
  GALLERY_DEFAULT_TWEAK_BUNDLE,
  TeamsGalleryTweaksContext,
  type TeamsGalleryTweakBundle,
} from "@/components/ui/Cteamsgallery/gallery.tweaks-context";

/** The tweaks2 context the landing's still-open design axes register under. */
const LANDING_TWEAKS_CONTEXT_NAME = "landing";

/** Registered option lists — every variant of a still-open rail question
 *  except its code default. The default is the v2 mechanism's implicit
 *  "existing" option (the fall-through on all-false booleans), so registering
 *  it would duplicate the panel's existing-rendering row. */
const RAIL_EXPANSION_TWEAK_OPTIONS = Object.values(RailExpansionVariant).filter(
  (variant) => variant !== DEFAULT_RAIL_EXPANSION_VARIANT,
);
const RAIL_PULSE_TWEAK_OPTIONS = Object.values(RailPulseVariant).filter(
  (variant) => variant !== DEFAULT_RAIL_PULSE_VARIANT,
);
const RAIL_EDIT_CONTEXT_TWEAK_OPTIONS = Object.values(
  RailEditContextVariant,
).filter((variant) => variant !== DEFAULT_RAIL_EDIT_CONTEXT_VARIANT);
const RAIL_FOLD_SURFACE_TWEAK_OPTIONS = Object.values(
  RailFoldSurfaceVariant,
).filter((variant) => variant !== DEFAULT_RAIL_FOLD_SURFACE_VARIANT);

/** The v2 registration spec — one mark per still-open landing question (the
 *  mark names are the v1 group keys, kept verbatim for provenance).
 *  Registering through `useTweaks` creates the marks in
 *  `app/dev/tweaks/contexts/landing.json`; the /dev/tweaks page and the
 *  handler CLI drive selections from there. */
const LANDING_TWEAKS_SPEC = {
  railexpand: RAIL_EXPANSION_TWEAK_OPTIONS,
  railpulse: RAIL_PULSE_TWEAK_OPTIONS,
  raileditcontext: RAIL_EDIT_CONTEXT_TWEAK_OPTIONS,
  railfold: RAIL_FOLD_SURFACE_TWEAK_OPTIONS,
};

interface TeamsGalleryTweaksHostProps {
  children: ReactNode;
}

/**
 * The landing-level owner of every teams-gallery design axis, wired to
 * tweaks v2.
 *
 * The state lives here rather than inside `Cteamsgallery` so a selection
 * spans every landing-reached surface — the deck, the standalone team overlay
 * (hero inspect / `?viewTeam=`), and the deploy preflight — including the
 * surfaces opened WITHOUT the deck. `createPortal` children stay inside the
 * React tree, so the portaled overlays see the provider too.
 *
 * The four still-open rail axes resolve from the "landing" tweaks context's
 * booleans (a /dev/tweaks preview or pick drives exactly one true); every
 * ratified axis reads from `GALLERY_DEFAULT_TWEAK_BUNDLE`. With no selection,
 * in production, or with the tweaks provider absent, all booleans are false
 * and the whole bundle is the production design.
 */
export function TeamsGalleryTweaksHost({
  children,
}: TeamsGalleryTweaksHostProps): ReactElement {
  const tweaks = useTweaks(LANDING_TWEAKS_CONTEXT_NAME, LANDING_TWEAKS_SPEC);
  const values = useMemo<TeamsGalleryTweakBundle>(
    () => ({
      ...GALLERY_DEFAULT_TWEAK_BUNDLE,
      railExpansionVariant: resolveTweakedVariant({
        tweaks,
        options: RAIL_EXPANSION_TWEAK_OPTIONS,
        fallback: DEFAULT_RAIL_EXPANSION_VARIANT,
      }),
      railPulseVariant: resolveTweakedVariant({
        tweaks,
        options: RAIL_PULSE_TWEAK_OPTIONS,
        fallback: DEFAULT_RAIL_PULSE_VARIANT,
      }),
      railEditContextVariant: resolveTweakedVariant({
        tweaks,
        options: RAIL_EDIT_CONTEXT_TWEAK_OPTIONS,
        fallback: DEFAULT_RAIL_EDIT_CONTEXT_VARIANT,
      }),
      railFoldSurfaceVariant: resolveTweakedVariant({
        tweaks,
        options: RAIL_FOLD_SURFACE_TWEAK_OPTIONS,
        fallback: DEFAULT_RAIL_FOLD_SURFACE_VARIANT,
      }),
    }),
    [tweaks],
  );

  return (
    <TeamsGalleryTweaksContext.Provider value={values}>
      {children}
    </TeamsGalleryTweaksContext.Provider>
  );
}
